import {
  isSessionExpiry,
  notifySessionExpiry,
  deadlineFromResponse,
  notifySessionDeadline,
} from "./session-expiry.js";

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Builds the ApiError for a failed response from its already-parsed (or
 * null, if parsing failed/was skipped) JSON body. Exported so
 * attachmentsApi.upload can throw the identical error shape: it bypasses
 * apiFetch on purpose (a multipart body must not carry a JSON content-type),
 * but must still fail the same way, or error handling downstream
 * (apps/web/src/i18n/api-error.ts) can't key off `.code`.
 */
export function apiErrorFromResponse(res: Response, json: unknown): ApiError {
  const e = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error ?? {};
  return new ApiError(e.code ?? "Internal", res.status, e.message ?? res.statusText, e.details);
}


export async function apiFetch<T>(
  url: string,
  init?: RequestInit,
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  // Only announce a JSON body when one is actually sent. Fastify rejects a
  // request carrying content-type: application/json with an empty body as 400
  // FST_ERR_CTP_EMPTY_JSON_BODY, which silently broke every bodyless mutation
  // in the app: logout, all deletes, risk/measure and measure/event un-linking,
  // and the docs reindex.
  // `...init` must stay ABOVE `headers`, otherwise a caller passing its own
  // headers replaces this merged object wholesale instead of extending it.
  const res = await fetchImpl(url, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init?.body === undefined || init.body === null
        ? {}
        : { "content-type": "application/json" }),
      ...(init?.headers ?? {}),
    },
  });
  // Every authenticated response carries how long the session has left, which
  // is what lets an otherwise idle tab notice it ending. Read before the status
  // checks below so it is picked up from error responses too.
  const deadline = deadlineFromResponse(res);
  if (deadline !== null) notifySessionDeadline(deadline);

  if (res.status === 204) return null as T;
  const json = res.headers.get("content-type")?.includes("application/json") ? await res.json() : null;
  if (!res.ok) {
    // Announce a lapsed session once, centrally, before the error reaches the
    // caller. AuthProvider clears the cached user, RequireAuth then routes to
    // the login page, and LoginPage explains why — the caller still gets its
    // ApiError and can render whatever it would have rendered anyway.
    if (isSessionExpiry(url, res.status)) notifySessionExpiry();
    throw apiErrorFromResponse(res, json);
  }
  return json as T;
}
